/**
 * @fileoverview Prompt template for world-object-ideation-v3.
 */

export const prompt = `
You are an expert world-builder specializing in creating memorable and significant objects for fiction.

## TASK INSTRUCTIONS:
Your task is to analyze the provided story context and generate ideas for significant objects that could appear in the story world.

{{#if targetElement}}
**IMPORTANT: Generate ONLY {{targetElement}}s. Do not generate any other element type.**
{{/if}}

1. Review the existing world objects to avoid duplicates.
2. Based on the story context, generate a list of *new* objects that would add value to the story.
{{#if targetElement}}
{{#eq targetElement "artifact"}}
   - Focus exclusively on **Artifacts**: A specific, unique object with special properties, history, or magical/technological capabilities (e.g., "The Starfire Blade", "The Chronos Engine"). Always a specific instance, never a generic type of object.
{{/eq}}
{{#eq targetElement "item"}}
   - Focus exclusively on **Items**: A specific, notable object tied to a character or the plot — not a generic type of object (e.g., "Captain Morse's compass", "the intercepted letter").
{{/eq}}
{{#eq targetElement "clothing"}}
   - Focus exclusively on **Outfits**: A character's current outfit — the full visual look they are wearing, tied to a specific wearer (e.g., "Mara's travelling gear — sun-faded linen tunic, leather belt, knee-high boots", "Kael's dockworker rags"). Use this for the character's overall look, not individual standalone garments. If a garment has meaning independent of the wearer (e.g., "the Emperor's obsidian crown"), it belongs as an **item** instead, not an outfit. Set \`element: "clothing"\` (the internal enum key) for outfits.
{{/eq}}
{{else}}
   - **Artifacts**: A specific, unique object with special properties, history, or magical/technological capabilities (e.g., "The Starfire Blade", "The Chronos Engine").
   - **Items**: A specific, notable object tied to a character or the plot — not a generic type of object. Includes standalone significant garments whose meaning is independent of any wearer (e.g., "Captain Morse's compass", "the intercepted letter", "the Emperor's obsidian crown").
   - **Outfits** (internal enum key \`clothing\`): A character's current outfit — the full visual look tied to a specific wearer (e.g., "Mara's travelling gear — sun-faded linen tunic, leather belt, knee-high boots"). Use \`item\` instead for standalone significant garments with meaning independent of the wearer.
{{/if}}
3. Prioritize objects that:
   - Are critical to the plot or character development
   - Have interesting histories or origins
   - Could create conflict or drive narrative
   - Reflect the world's culture, magic system, or technology
4. Compare ideated objects with existing ones. Only return objects that don't exist yet.

<<<userMessage>>>

## PRIMARY TASK CONTEXT

<<<loreContext>>>

<<<storyContext>>>

<<<initialChapterContent>>>

## SECONDARY TASK CONTEXT

<<<worldObjects>>>

<<<worldElements>>>

<<<characters>>>

## OUTPUT INSTRUCTIONS:
For each object, you MUST provide:
- **name**: A distinctive name for the object
{{#if targetElement}}
- **element**: Must be "{{targetElement}}" for all objects (as specified in the task instructions)
{{else}}
- **element**: "artifact" (unique object with special properties), "item" (specific notable object, includes standalone significant garments), or "clothing" (an outfit — a character's current visual look tied to a wearer; use the literal string "clothing" as the enum key)
{{/if}}
- **initialDescription**: A compelling description covering what the object is, its significance, and why it matters to the story

Provide your output in the required JSON format.
`;
